import { MAX_LEVEL, gearAttackMult, gearHpMult } from '../content/balance';
import type { PassiveRef } from '../sim/passives';
import { registry } from '../sim/registry';
import { type BaseStats, defaultBaseStats } from '../sim/stats';
import {
  type CardCollection,
  type GemSocket,
  MAX_ACTIVE_SIGILS,
  SIGILS,
  cardSetBonuses,
  collectionHpBonus,
  gemEffect,
  sigilLevel,
  stoneSigilPoints,
} from './build';
import {
  ACC_SLOTS,
  type AccessoryItem,
  type CombatStat,
  type EquipItem,
  GEAR_SLOTS,
  type GearItem,
  type GearSlot,
  SET_BONUSES,
  type StoneItem,
  gearIlvl,
} from './items';

// ---------------------------------------------------------------- XP & skill points

/** XP needed to go from `level` to `level + 1` (research 02 §11.3, compressed). */
export const xpToNext = (level: number): number => (level >= MAX_LEVEL ? Infinity : Math.round((150 * Math.pow(level, 1.55)) / 10) * 10);

export const SKILL_POINTS_PER_LEVEL = 2;
/** Skill points needed to raise a skill *to* level n (index = n). Tripod tiers unlock at 4/7/10. */
export const SKILL_LEVEL_COST = [0, 0, 1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 5];
export const SKILL_MAX_LEVEL = 12;
export const cumulativeSkillCost = (level: number): number => SKILL_LEVEL_COST.slice(0, level + 1).reduce((a, b) => a + b, 0);

// ---------------------------------------------------------------- save types

export interface InventoryState {
  stacks: Record<string, number>;
  items: EquipItem[];
  capacity: number;
}

export interface QuestProgress {
  id: string;
  step: number;
  counts: number[];
  accepted: number;
}

export interface QuestState {
  active: Record<string, QuestProgress>;
  done: string[];
  tracked: string[];
}

export interface DailyState {
  day: number;
  riftEnergy: number;
  riftRested: number;
  colossusClaimed: boolean;
  colossusRested: number;
  bounties: string[];
  bountiesDone: string[];
  fieldBoss: boolean;
}

export interface WeeklyState {
  week: number;
  deepvault: number[];
  siege: number[];
  siegeMode: 'normal' | 'hard' | null;
  bounties: string[];
  bountiesDone: string[];
}

export interface CharacterSave {
  id: string;
  name: string;
  classId: string;
  createdAt: number;
  playTime: number;
  level: number;
  xp: number;
  zone: string;
  spawn: string;
  skillLevels: Record<string, number>;
  tripods: Record<string, (string | null)[]>;
  bar: (string | null)[];
  awakening: string | null;
  bonusSkillPoints: number;
  equipment: Partial<Record<GearSlot, GearItem>> & Partial<Record<(typeof ACC_SLOTS)[number], AccessoryItem>> & { stone?: StoneItem };
  inventory: InventoryState;
  battleSlots: (string | null)[];
  sigilBooks: Record<string, number>;
  activeSigils: string[];
  gems: (GemSocket | null)[];
  quests: QuestState;
  flags: Record<string, number | boolean | string>;
  waystones: string[];
  bonds: Record<string, number>;
  /** Friends list (other characters, with the key of each friendship's private room). */
  friends?: { uid: string; name: string; classId: string; key: string }[];
  lifeSkills: Record<string, { level: number; xp: number }>;
  titles: string[];
  title: string | null;
  counters: Record<string, number>;
  daily: DailyState;
  weekly: WeeklyState;
}

export const LIFE_SKILLS = ['foraging', 'logging', 'mining', 'fishing', 'hunting', 'excavating'] as const;
export type LifeSkill = (typeof LIFE_SKILLS)[number];

export function newCharacter(id: string, name: string, classId: string): CharacterSave {
  const cls = registry.cls(classId);
  return {
    id,
    name,
    classId,
    createdAt: Date.now(),
    playTime: 0,
    level: 1,
    xp: 0,
    zone: 'emberwild',
    spawn: 'start',
    skillLevels: Object.fromEntries(cls.skills.map((s) => [s.id, 1])),
    tripods: {},
    bar: [...cls.defaultBar],
    awakening: cls.awakenings[0]?.id ?? null,
    bonusSkillPoints: 0,
    equipment: {},
    inventory: { stacks: { potion_small: 5 }, items: [], capacity: 60 },
    battleSlots: ['potion_small', null, null, null],
    sigilBooks: {},
    activeSigils: [],
    gems: Array.from({ length: 6 }, () => null),
    quests: { active: {}, done: [], tracked: [] },
    flags: {},
    waystones: [],
    bonds: {},
    lifeSkills: Object.fromEntries(LIFE_SKILLS.map((k) => [k, { level: 1, xp: 0 }])),
    titles: [],
    title: null,
    counters: {},
    daily: { day: 0, riftEnergy: 100, riftRested: 0, colossusClaimed: false, colossusRested: 0, bounties: [], bountiesDone: [], fieldBoss: false },
    weekly: { week: 0, deepvault: [], siege: [], siegeMode: null, bounties: [], bountiesDone: [] },
  };
}

// ---------------------------------------------------------------- derived numbers

export function totalSkillPoints(c: CharacterSave): number {
  return (c.level - 1) * SKILL_POINTS_PER_LEVEL + c.bonusSkillPoints;
}

export function spentSkillPoints(c: CharacterSave): number {
  let n = 0;
  for (const lvl of Object.values(c.skillLevels)) n += cumulativeSkillCost(lvl);
  return n;
}

export function freeSkillPoints(c: CharacterSave): number {
  return totalSkillPoints(c) - spentSkillPoints(c);
}

/** Average item level of the six gear pieces (empty slots count as 0). */
export function itemLevel(c: CharacterSave): number {
  let sum = 0;
  for (const slot of GEAR_SLOTS) {
    const g = c.equipment[slot];
    if (g) sum += gearIlvl(g);
  }
  return Math.round((sum / GEAR_SLOTS.length) * 100) / 100;
}

export function combatStats(c: CharacterSave): Record<CombatStat, number> {
  const out: Record<CombatStat, number> = { crit: 0, spec: 0, swift: 0, dom: 0, endur: 0, expert: 0 };
  for (const slot of ACC_SLOTS) {
    const a = c.equipment[slot];
    if (!a) continue;
    for (const [k, v] of Object.entries(a.stats) as [CombatStat, number][]) out[k] += v;
  }
  return out;
}

/** Sigil points from books, accessories and the rune stone. */
export function sigilPoints(c: CharacterSave): Record<string, number> {
  const pts: Record<string, number> = { ...c.sigilBooks };
  for (const slot of ACC_SLOTS) {
    const a = c.equipment[slot];
    if (!a) continue;
    for (const s of a.sigils) pts[s.id] = (pts[s.id] ?? 0) + s.pts;
  }
  const stone = c.equipment.stone;
  if (stone) for (const s of stoneSigilPoints(stone)) pts[s.id] = (pts[s.id] ?? 0) + s.pts;
  return pts;
}

/** Active sigils: the player's chosen positive sigils (max 5) + every negative one reaching Lv 1. */
export function activeSigils(c: CharacterSave): { id: string; level: number }[] {
  const pts = sigilPoints(c);
  const out: { id: string; level: number }[] = [];
  for (const id of c.activeSigils.slice(0, MAX_ACTIVE_SIGILS)) {
    const lvl = sigilLevel(pts[id] ?? 0);
    if (lvl > 0) out.push({ id, level: lvl });
  }
  for (const [id, p] of Object.entries(pts)) {
    if (SIGILS[id]?.negative && sigilLevel(p) > 0) out.push({ id, level: sigilLevel(p) });
  }
  return out;
}

export function setPieces(c: CharacterSave): Record<string, number> {
  const out: Record<string, number> = {};
  for (const slot of GEAR_SLOTS) {
    const g = c.equipment[slot];
    if (g?.set) out[g.set] = (out[g.set] ?? 0) + 1;
  }
  return out;
}

export interface AccountBonuses {
  /** Legacy (roster) rank. */
  legacyRank: number;
  /** Collectible bonuses (hearts etc.). */
  collectHp: number;
  collectAtk: number;
  cards: CardCollection;
}

export interface BuiltCharacter {
  base: BaseStats;
  passives: PassiveRef[];
  /** Per-skill modifiers from gems. */
  skillMods: Record<string, { dmg?: number; cd?: number }>;
  awakeningUses: number;
  ilvl: number;
  combat: Record<CombatStat, number>;
  summary: { attack: number; maxHp: number; critRate: number; critDmg: number; atkSpeed: number; moveSpeed: number; cdr: number; defense: number };
}

/**
 * Folds class base stats, level, gear, accessories, sigils, cards, gems and
 * account bonuses into the sim's BaseStats. Pure: same save → same numbers.
 */
export function buildCharacter(c: CharacterSave, acct: AccountBonuses, opts: { pvp?: boolean } = {}): BuiltCharacter {
  const cls = registry.cls(c.classId);
  const b: BaseStats = { ...defaultBaseStats(), ...cls.baseStats(c.level), level: c.level };

  // Gear: weapon drives attack, armour drives HP.
  const weapon = c.equipment.weapon;
  const armor = GEAR_SLOTS.filter((s) => s !== 'weapon').map((s) => c.equipment[s]);
  const weaponIlvl = weapon ? gearIlvl(weapon) : 0;
  const armorIlvl = armor.reduce((sum, g) => sum + (g ? gearIlvl(g) : 0), 0) / 5;
  const powerIlvl = weaponIlvl * 0.5 + armorIlvl * 0.5;
  let atkMult = gearAttackMult(powerIlvl);
  let hpMult = gearHpMult(armorIlvl);
  let dmgBonus = 0;
  let critAdd = 0;
  let critDmgAdd = 0;
  let atkSpeedAdd = 0;
  let moveSpeedAdd = 0;
  let dmgReduction = 0;
  let defMult = 1;
  let healMult = 1;
  let backHead = 0;

  // Quality: weapon quality adds damage, armour quality adds HP.
  if (weapon) dmgBonus += 0.1 * (weapon.quality / 100);
  for (const g of armor) if (g) hpMult += 0.012 * (g.quality / 100);

  // Set bonuses.
  for (const [set, n] of Object.entries(setPieces(c))) {
    for (const bonus of SET_BONUSES[set] ?? []) {
      if (n < bonus.pieces) continue;
      dmgBonus += bonus.mods.dmg ?? 0;
      critAdd += bonus.mods.crit ?? 0;
      critDmgAdd += bonus.mods.critDmg ?? 0;
      hpMult += bonus.mods.hp ?? 0;
      atkSpeedAdd += bonus.mods.atkSpeed ?? 0;
      dmgReduction += bonus.mods.dmgReduction ?? 0;
      backHead += bonus.mods.backHead ?? 0;
    }
  }

  // Accessories: HP + combat stats.
  let accHp = 0;
  for (const slot of ACC_SLOTS) {
    const a = c.equipment[slot];
    if (a) accHp += 0.015 + (a.quality / 100) * 0.01;
  }
  hpMult += accHp;
  const cs = combatStats(c);
  if (opts.pvp) {
    // PvP normalisation: fixed stat budget.
    cs.crit = 500;
    cs.spec = 300;
    cs.swift = 200;
    cs.dom = 0;
    cs.endur = 0;
    cs.expert = 0;
  }
  critAdd += cs.crit / 2800;
  const specBonus = cs.spec / 2000;
  atkSpeedAdd += cs.swift / 5800;
  moveSpeedAdd += cs.swift / 5800;
  const cdrAdd = cs.swift / 4700;
  const vsCC = cs.dom / 2800;
  dmgReduction += cs.endur / 9000;
  healMult += cs.expert / 3000;

  // Sigils: static parts.
  const sigils = opts.pvp ? [] : activeSigils(c);
  for (const s of sigils) {
    const m = SIGILS[s.id]?.stats?.(s.level);
    if (!m) continue;
    atkMult *= 1 + (m.atkPct ?? 0);
    critAdd += m.critRate ?? 0;
    critDmgAdd += m.critDmg ?? 0;
    atkSpeedAdd += m.atkSpeed ?? 0;
    moveSpeedAdd += m.moveSpeed ?? 0;
    defMult *= 1 + (m.defensePct ?? 0);
    healMult += m.healingReceived ?? 0;
    dmgReduction -= m.dmgTaken ?? 0;
  }

  // Cards (roster-wide collection).
  if (!opts.pvp) {
    hpMult += collectionHpBonus(acct.cards);
    for (const bonus of cardSetBonuses(acct.cards)) {
      dmgBonus += bonus.mods.dmg ?? 0;
      hpMult += bonus.mods.hp ?? 0;
      dmgReduction += bonus.mods.dmgReduction ?? 0;
      critAdd += bonus.mods.crit ?? 0;
    }
  }

  // Legacy rank + collectibles.
  atkMult *= 1 + acct.legacyRank * 0.004 + acct.collectAtk;
  hpMult *= 1 + acct.legacyRank * 0.004 + acct.collectHp;

  const base: BaseStats = {
    ...b,
    attack: b.attack * atkMult,
    maxHp: Math.round(b.maxHp * hpMult),
    defense: b.defense * defMult,
    critRate: b.critRate + critAdd,
    critDmg: b.critDmg + critDmgAdd,
    atkSpeed: b.atkSpeed + atkSpeedAdd,
    moveSpeed: b.moveSpeed * (1 + moveSpeedAdd),
    cdr: b.cdr + cdrAdd,
    dmgBonus: b.dmgBonus + dmgBonus,
    specBonus: b.specBonus + specBonus,
    vsCCBonus: b.vsCCBonus + vsCC,
    dmgReduction: Math.max(-0.5, b.dmgReduction + dmgReduction),
    backBonus: b.backBonus + backHead,
    headBonus: b.headBonus + backHead,
    identityGainMult: b.identityGainMult * (1 + cs.spec / 4000),
    healingReceivedMult: Math.max(0.1, healMult),
  };

  const passives: PassiveRef[] = sigils.map((s) => ({ id: s.id, level: s.level }));
  const skillMods: Record<string, { dmg?: number; cd?: number }> = {};
  if (!opts.pvp) {
    for (const gem of c.gems) {
      if (!gem) continue;
      const m = (skillMods[gem.skillId] ??= {});
      if (gem.type === 'dmg') m.dmg = (m.dmg ?? 0) + gemEffect('dmg', gem.level);
      else m.cd = (m.cd ?? 0) + gemEffect('cd', gem.level);
    }
  }
  const apex = sigils.find((s) => s.id === 'apex_mastery');
  const awakeningUses = 3 + (apex?.level ?? 0);

  return {
    base,
    passives,
    skillMods,
    awakeningUses,
    ilvl: itemLevel(c),
    combat: cs,
    summary: {
      attack: Math.round(base.attack),
      maxHp: base.maxHp,
      critRate: Math.min(1, base.critRate),
      critDmg: base.critDmg,
      atkSpeed: base.atkSpeed,
      moveSpeed: base.moveSpeed,
      cdr: Math.min(0.6, base.cdr),
      defense: Math.round(base.defense),
    },
  };
}
